import * as THREE from 'three';
import { createCloudSim, type CloudSim } from './cloudSim';
import { planCloudBurst, plannedFineCount } from './cloudSpawn';
import { createGrainSim, type GrainSim } from './grainSim';
import { createGroundMesh, makeDummyHeightTexture, syncGroundHeight, syncGroundShadow } from './ground';
import { createGroundShadow, type GroundShadow } from './groundShadow';
import { createHeightfield, type ArenaXZ, type HeightfieldSim, type HeightSample } from './heightfield';
import { bakeBaseElevation } from './baseElevation';
import {
  createChunkSim,
  DEFAULT_CHIP_CAP,
  DEFAULT_CHUNK_CAP,
  planChipBurst,
  planChunkBurst,
  plannedChunkCount,
  type ChunkSim,
} from './chunkSim';
import { planJointSchedule, type ScheduledFine } from './jointBleed';
import { probeFloatRt } from './gpuUtils';
import { splitBurstMass } from './massSplit';
import { createReducer } from './reduce';
import { clampEnergy } from '../../services/energy';
import { planImpactExtras, plannedImpactExtraFineCount, resolveImpact } from './impactRegime';
import { planBurst, plannedBurstCount, plannedChipCount } from './spawn';
import { cloudCapacityForTier, grainCapacityForTier, heightfieldSizeForTier } from './spawn';
import { allocateBurstBudgets, budgetPressure, poolBurstRoom, type PoolOccupancy } from './budget';
import {
  asNormalTriple,
  asTriple,
  type BoxCollider,
  type BurstParams,
  type LastBurstCall,
  type ParticleStats,
  type ParticleWorldOptions,
} from './types';

export interface ParticleWorld {
  readonly ready: boolean;
  burst(params: BurstParams): number;
  step(dt: number, camera?: THREE.Camera | null): void;
  attach(scene: THREE.Scene): void;
  detach(): void;
  reset(): void;
  setDust(on: boolean): void;
  setGrainsVisible(on: boolean): void;
  setSixWay(on: boolean): void;
  getSixWay(): boolean;
  setLightDir(x: number, y: number, z: number): void;
  burstMany(params: BurstParams[]): number;
  lastBurst(): LastBurstCall | null;
  readFinesMeanVelocity(): { x: number; y: number; z: number; count: number } | null;
  readFinesOpacity(): { meanAlpha: number; meanLife: number; count: number } | null;
  readFinesLive(): Array<{ x: number; y: number; z: number; vx: number; vy: number; vz: number }> | null;
  getStats(): ParticleStats;
  getPickables(): THREE.Object3D[];
  getHeightfieldTexture(): THREE.Texture | null;
  sampleHeightfieldAt(x: number, z: number): HeightSample | null;
  blitHeightfieldPreview(canvas: HTMLCanvasElement): void;
  dispose(): void;
}

const BOX_COLOR = 0x6a6e74;

function makeColliderMesh(box: BoxCollider): THREE.Mesh {
  const geom = new THREE.BoxGeometry(box.half[0] * 2, box.half[1] * 2, box.half[2] * 2);
  const mat = new THREE.MeshStandardMaterial({
    color: BOX_COLOR,
    roughness: 0.82,
    metalness: 0.04,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.position.set(box.center[0], box.center[1], box.center[2]);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

export function createParticleWorld(
  renderer: THREE.WebGLRenderer,
  opts: ParticleWorldOptions,
): ParticleWorld {
  const ready = probeFloatRt(renderer);
  const tier = opts.tier;
  const bounds = opts.arenaBounds;
  const arena: ArenaXZ = {
    minX: bounds.minX,
    minZ: bounds.minZ,
    sizeX: bounds.maxX - bounds.minX,
    sizeZ: bounds.maxZ - bounds.minZ,
  };
  const capacity = Math.max(0, (opts.grainCapacity ?? grainCapacityForTier(tier)) | 0);
  const cloudCap = Math.max(0, (opts.fineCapacity ?? cloudCapacityForTier(tier)) | 0);
  const hfSize = heightfieldSizeForTier(tier);
  const colliders = opts.colliders.slice(0, 16);

  let grains: GrainSim | null = null;
  let cloud: CloudSim | null = null;
  let chunks: ChunkSim | null = null;
  let shadow: GroundShadow | null = null;
  let heightfield: HeightfieldSim | null = null;
  let reducer: ReturnType<typeof createReducer> | null = null;
  let ground: THREE.Mesh | null = null;
  const boxGroup = new THREE.Group();
  boxGroup.name = 'particle-lab-colliders';
  for (const box of colliders) boxGroup.add(makeColliderMesh(box));

  let attached: THREE.Scene | null = null;
  let bursts = 0;
  let frame = 0;
  let liveGrains = 0;
  let liveFines = 0;
  let pileVolume = 0;
  let lastStepMs = 0;
  let pendingSpawns = 0;
  let forceReduce = true;
  let dustOn = true;
  let sixWayOn = true;
  let idleReposeLeft = 0;
  let lastBurstCall: LastBurstCall | null = null;
  let simTime = 0;
  let bleedEmissions = 0;
  let jointPuffEmissions = 0;
  const scheduledFines: Array<ScheduledFine & { absTime: number }> = [];

  if (ready) {
    heightfield = createHeightfield(renderer, hfSize, arena, colliders);
    if (capacity > 0) {
      grains = createGrainSim(renderer, capacity, arena, colliders, {
        spriteScale: tier === 'mobile' ? 0.42 : 1,
        spriteHard: tier === 'mobile' ? 1 : 0,
      });
    }
    if (cloudCap > 0) {
      cloud = createCloudSim(renderer, cloudCap, arena, colliders);
      cloud.setSixWay(sixWayOn);
    }
    const chunkCap = opts.disableChunks === true
      ? 0
      : (opts.chunkCapacity ?? DEFAULT_CHUNK_CAP);
    const chipCap = opts.disableChunks === true
      ? 0
      : (opts.chipCapacity ?? DEFAULT_CHIP_CAP);
    if (chunkCap > 0 || chipCap > 0) {
      const baseElev = bakeBaseElevation(hfSize, arena, colliders);
      chunks = createChunkSim({
        cap: Math.max(1, chunkCap),
        chipCap: Math.max(1, chipCap),
        colliders,
        arena,
        baseElevation: baseElev,
        baseSize: hfSize,
        floorOnlySettle: opts.chunkFloorOnlySettle === true,
      });
    }
    if (cloudCap > 0) {
      shadow = createGroundShadow(renderer, arena, cloudCap, opts.groundShadow);
    }
    reducer = createReducer(renderer, Math.max(grains?.texSize ?? 0, cloud?.texSize ?? 0, hfSize));
  }
  // Floor must render even if float RTs fail — never leave grains in a void.
  ground = createGroundMesh(
    heightfield?.texture() ?? makeDummyHeightTexture(),
    arena,
    tier,
    hfSize,
    heightfield?.baseTexture() ?? makeDummyHeightTexture(),
    colliders,
  );
  if (ground && shadow) syncGroundShadow(ground, shadow.texture());

  const grainOcc = (): PoolOccupancy => ({
    live: grains?.unexpiredCount(simTime) ?? 0,
    pending: 0,
    capacity,
  });
  const fineOcc = (): PoolOccupancy => ({
    live: cloud?.unexpiredCount(simTime) ?? 0,
    pending: scheduledFines.length,
    capacity: cloudCap,
  });

  const chunkSnap = () => chunks?.stats() ?? { live: 0, airborne: 0, settled: 0, held: 0 };

  const stats = (): ParticleStats => {
    const cs = chunkSnap();
    return {
      liveGrains,
      liveFines,
      liveChunks: cs.live,
      airborneChunks: cs.airborne,
      settledChunks: cs.settled,
      bleedEmissions,
      jointPuffEmissions,
      simTime,
      pileVolume,
      bursts,
      frame,
      capacity,
      fineCapacity: cloudCap,
      lastStepMs,
      pendingSpawns,
      budgetPressure: budgetPressure(grainOcc(), fineOcc()),
      sixWay: sixWayOn,
      tier,
    };
  };

  const fireOne = (params: BurstParams, grainMax: number, fineMax: number): { grains: number; fines: number; chunks: number; grainWanted: number; fineWanted: number; chunkWanted: number } => {
    const energy = clampEnergy(params.energy);
    const impact = params.impact;
    const resolved = resolveImpact(impact);
    const split = splitBurstMass(params.mass, params.profile, impact);
    const grainWanted = plannedBurstCount(params.mass, params.profile, 1e9, energy, impact);
    const chunkWanted = plannedChunkCount(energy, params.profile, 1e9, impact);
    const surfaceNormal = asNormalTriple(params.surfaceNormal);
    const pos = asTriple(params.position);
    const dir = asTriple(params.direction);
    const extraInput = {
      position: pos,
      energy,
      profile: params.profile,
      burstIndex: bursts,
      surfaceNormal,
      impact,
      colliders,
    };
    const extras = dustOn ? planImpactExtras(extraInput) : {
      fines: [] as ReturnType<typeof planImpactExtras>['fines'],
      chips: [] as ReturnType<typeof planImpactExtras>['chips'],
      backSpallFines: 0,
      backSpallChips: 0,
      ringFines: 0,
    };
    const joint = dustOn
      ? planJointSchedule({
        position: pos,
        energy,
        profile: params.profile,
        burstIndex: bursts,
        surfaceNormal,
        fineMassBudget: split.fineMass * 0.12,
        impact,
      })
      : { points: [], fines: [] };
    const jointMass = joint.fines.reduce((s, rec) => s + rec.record.depositMass, 0);
    const immediateFine = Math.max(0, split.fineMass - jointMass);
    const primaryFineWanted = dustOn && immediateFine > 0
      ? plannedFineCount(immediateFine, params.profile, energy, 1e9, impact)
      : 0;
    const fineWanted = primaryFineWanted + joint.fines.length + extras.fines.length;
    let n = 0;
    let f = 0;
    let c = 0;
    if (grains && grainMax > 0) {
      const records = planBurst({
        position: pos,
        direction: dir,
        energy,
        mass: params.mass,
        profile: params.profile,
        burstIndex: bursts,
        maxCount: grainMax,
        surfaceNormal,
        impact,
      });
      n = grains.enqueue(records, simTime);
      pendingSpawns += n;
    }
    if (dustOn && cloud && immediateFine > 0 && fineMax > 0) {
      const fines = planCloudBurst({
        position: pos,
        direction: dir,
        energy,
        fineMass: immediateFine,
        profile: params.profile,
        burstIndex: bursts,
        maxCount: fineMax,
        surfaceNormal,
        impact,
      });
      f = cloud.enqueue(fines, simTime);
      pendingSpawns += f;
    }
    if (dustOn && cloud && extras.fines.length > 0) {
      const room = Math.max(0, fineMax - f);
      const extraFines = room < extras.fines.length ? extras.fines.slice(0, room) : extras.fines;
      const extraN = cloud.enqueue(extraFines, simTime);
      f += extraN;
      pendingSpawns += extraN;
    }
    if (dustOn && cloud && joint.fines.length > 0) {
      for (const rec of joint.fines) {
        scheduledFines.push({ ...rec, absTime: simTime + rec.emitAt });
      }
    }
    if (chunks && split.chunkMass > 0 && chunkWanted > 0) {
      const recs = planChunkBurst({
        position: pos,
        direction: dir,
        energy,
        chunkMass: split.chunkMass,
        profile: params.profile,
        burstIndex: bursts,
        surfaceNormal,
        impact,
      });
      c = chunks.enqueue(recs, simTime);
    }
    if (chunks) {
      const chipWanted = plannedChipCount(params.mass, params.profile, 1e9, energy, impact);
      if (chipWanted > 0) {
        const chips = planChipBurst({
          position: pos,
          direction: dir,
          energy,
          mass: params.mass,
          chipMass: split.grainMass * params.profile.signature.chipFraction,
          profile: params.profile,
          burstIndex: bursts,
          surfaceNormal,
          impact,
        });
        c += chunks.enqueueChips(chips, simTime);
      }
      if (extras.chips.length > 0 && params.profile.signature.chipCap > 0) {
        c += chunks.enqueueChips(extras.chips, simTime);
      }
    }
    lastBurstCall = {
      position: pos,
      direction: dir,
      energy,
      mass: params.mass,
      profileId: params.profile.id,
      grains: n,
      fines: f,
      chunks: c,
      grainWanted,
      fineWanted,
      chunkWanted,
      surfaceNormal,
      kind: resolved.kind,
      intensity: resolved.intensity,
      blastFraction: resolved.blastFraction,
      backSpallFines: extras.backSpallFines,
      backSpallChips: chunks ? extras.backSpallChips : 0,
      ringFines: extras.ringFines,
    };
    bursts += 1;
    forceReduce = true;
    return { grains: n, fines: f, chunks: c, grainWanted, fineWanted, chunkWanted };
  };

  const reduceNow = () => {
    if (!ready || !heightfield || !reducer) return;
    liveGrains = grains
      ? reducer.countAlive(renderer, grains.posTexture(), grains.texSize)
      : 0;
    liveFines = dustOn && cloud
      ? reducer.countAlive(renderer, cloud.posTexture(), cloud.texSize)
      : 0;
    // Visual displacement volume (Σ (R+A) × cellArea). R = granular, A = film.
    // Fine distance falloff / haze skip reduce visual height without changing
    // depositMass on the particle ledger — pileVolume tracks what you see, not kg.
    const heightSum = reducer.sumDeposit(renderer, heightfield.texture(), heightfield.size);
    pileVolume = heightSum * heightfield.cellArea;
    forceReduce = false;
  };

  return {
    ready,
    burst(params) {
      if (!grains && !cloud) return 0;
      const gRoom = poolBurstRoom(grainOcc());
      const fRoom = poolBurstRoom(fineOcc());
      const energy = clampEnergy(params.energy);
      const grainWanted = plannedBurstCount(params.mass, params.profile, 1e9, energy, params.impact);
      const split = splitBurstMass(params.mass, params.profile, params.impact);
      const extras = dustOn
        ? plannedImpactExtraFineCount({
          position: asTriple(params.position),
          energy,
          profile: params.profile,
          burstIndex: bursts,
          surfaceNormal: asNormalTriple(params.surfaceNormal),
          impact: params.impact,
          colliders,
        })
        : 0;
      const fineWanted = (dustOn && split.fineMass > 0
        ? plannedFineCount(split.fineMass, params.profile, energy, 1e9, params.impact)
        : 0) + extras;
      const grainMax = allocateBurstBudgets([grainWanted], gRoom)[0];
      const fineMax = allocateBurstBudgets([fineWanted], fRoom)[0];
      return fireOne(params, grainMax, fineMax).grains;
    },
    burstMany(list) {
      if ((!grains && !cloud) || list.length === 0) return 0;
      const gRoom = poolBurstRoom(grainOcc());
      const fRoom = poolBurstRoom(fineOcc());
      const grainWants = list.map((p) => plannedBurstCount(p.mass, p.profile, 1e9, clampEnergy(p.energy), p.impact));
      const fineWants = list.map((p, i) => {
        if (!dustOn) return 0;
        const split = splitBurstMass(p.mass, p.profile, p.impact);
        const extras = plannedImpactExtraFineCount({
          position: asTriple(p.position),
          energy: clampEnergy(p.energy),
          profile: p.profile,
          burstIndex: bursts + i,
          surfaceNormal: asNormalTriple(p.surfaceNormal),
          impact: p.impact,
          colliders,
        });
        return (split.fineMass > 0 ? plannedFineCount(split.fineMass, p.profile, clampEnergy(p.energy), 1e9, p.impact) : 0) + extras;
      });
      const grainMax = allocateBurstBudgets(grainWants, gRoom);
      const fineMax = allocateBurstBudgets(fineWants, fRoom);
      let spawned = 0;
      for (let i = 0; i < list.length; i += 1) {
        spawned += fireOne(list[i], grainMax[i], fineMax[i]).grains;
      }
      return spawned;
    },
    step(dt, camera) {
      const t0 = performance.now();
      const clamped = Math.min(Math.max(dt, 0), 0.05);
      simTime += clamped;
      if (ready && heightfield && ground) {
        if (dustOn && cloud && scheduledFines.length > 0) {
          const due: typeof scheduledFines = [];
          let kept = 0;
          for (let i = 0; i < scheduledFines.length; i += 1) {
            const rec = scheduledFines[i];
            if (rec.absTime <= simTime) due.push(rec);
            else {
              scheduledFines[kept] = rec;
              kept += 1;
            }
          }
          scheduledFines.length = kept;
          if (due.length > 0) {
            cloud.enqueue(due.map((d) => d.record), simTime);
            pendingSpawns += due.length;
            for (const d of due) {
              if (d.role === 'bleed') bleedEmissions += 1;
              else jointPuffEmissions += 1;
            }
          }
        }
        if (grains) {
          grains.step(renderer, dt, heightfield.texture(), heightfield.baseTexture(), liveGrains);
        }
        if (dustOn && cloud) {
          cloud.step(renderer, dt, heightfield.texture(), heightfield.baseTexture(), camera, liveFines);
          if (shadow && ground) {
            shadow.step(
              renderer,
              cloud.posTexture(),
              cloud.velTexture(),
              cloud.metaTexture(),
              cloud.texSize,
              liveFines,
            );
            syncGroundShadow(ground, shadow.texture());
          }
        } else if (shadow && ground) {
          shadow.clear(renderer);
          syncGroundShadow(ground, shadow.texture());
        }
        if (grains) {
          heightfield.splat(
            renderer,
            grains.posTexture(),
            grains.metaTexture(),
            grains.texSize,
            grains.capacity,
            grains.originTexture(),
          );
        }
        if (dustOn && cloud) {
          heightfield.splatFines(
            renderer,
            cloud.posTexture(),
            cloud.metaTexture(),
            cloud.texSize,
            cloud.capacity,
            cloud.originTexture(),
            cloud.velTexture(),
          );
        }
        if (chunks) {
          const recycled = chunks.step(clamped, simTime, camera);
          for (const s of recycled) {
            heightfield.splatVolume(renderer, s.x, s.y, s.z, s.mass, s.materialId);
          }
        }
        if (forceReduce || frame % 4 === 0) reduceNow();
        if (liveGrains > 0 || liveFines > 0) idleReposeLeft = 12;
        if (idleReposeLeft > 0) {
          heightfield.repose(renderer);
          if (frame % 2 === 0) heightfield.repose(renderer);
          idleReposeLeft -= 1;
        }
        syncGroundHeight(ground, heightfield.texture());
      }
      frame += 1;
      lastStepMs = performance.now() - t0;
    },
    attach(scene) {
      if (attached) this.detach();
      attached = scene;
      if (ground) scene.add(ground);
      if (grains) scene.add(grains.mesh);
      if (cloud) scene.add(cloud.mesh);
      if (chunks) {
        scene.add(chunks.mesh);
        scene.add(chunks.chipMesh);
      }
      scene.add(boxGroup);
    },
    detach() {
      if (!attached) return;
      if (ground) attached.remove(ground);
      if (grains) attached.remove(grains.mesh);
      if (cloud) attached.remove(cloud.mesh);
      if (chunks) {
        attached.remove(chunks.mesh);
        attached.remove(chunks.chipMesh);
      }
      attached.remove(boxGroup);
      attached = null;
    },
    reset() {
      bursts = 0;
      frame = 0;
      liveGrains = 0;
      liveFines = 0;
      pileVolume = 0;
      pendingSpawns = 0;
      idleReposeLeft = 0;
      lastBurstCall = null;
      simTime = 0;
      bleedEmissions = 0;
      jointPuffEmissions = 0;
      scheduledFines.length = 0;
      if (grains) grains.reset(renderer);
      if (cloud) cloud.reset(renderer);
      if (chunks) chunks.reset();
      if (shadow) shadow.clear(renderer);
      if (heightfield) heightfield.reset(renderer);
      if (ground && heightfield) syncGroundHeight(ground, heightfield.texture());
      forceReduce = true;
    },
    setDust(on) {
      dustOn = on;
      if (cloud) cloud.setVisible(on);
      if (!on && cloud) {
        cloud.killAll(renderer);
        liveFines = 0;
        forceReduce = true;
      }
    },
    setGrainsVisible(on) {
      if (grains) grains.mesh.visible = on;
    },
    setSixWay(on) {
      sixWayOn = on;
      cloud?.setSixWay(on);
    },
    getSixWay() {
      return sixWayOn;
    },
    setLightDir(x, y, z) {
      cloud?.setLightDir(x, y, z);
    },
    lastBurst() {
      return lastBurstCall;
    },
    readFinesMeanVelocity() {
      if (!cloud) return null;
      return cloud.readMeanVelocity(renderer);
    },
    readFinesOpacity() {
      if (!cloud) return null;
      return cloud.readOpacity(renderer);
    },
    readFinesLive() {
      if (!cloud) return null;
      return cloud.readLive(renderer);
    },
    getStats: stats,
    getPickables() {
      const list: THREE.Object3D[] = [];
      if (ground) list.push(ground);
      for (const child of boxGroup.children) list.push(child);
      return list;
    },
    getHeightfieldTexture() {
      return heightfield?.texture() ?? null;
    },
    sampleHeightfieldAt(x, z) {
      if (!heightfield) return null;
      return heightfield.sampleAt(renderer, x, z);
    },
    blitHeightfieldPreview(canvas) {
      heightfield?.blitPreview(renderer, canvas);
    },
    dispose() {
      this.detach();
      grains?.dispose();
      cloud?.dispose();
      chunks?.dispose();
      shadow?.dispose();
      heightfield?.dispose();
      reducer?.dispose();
      if (ground) {
        ground.traverse((obj) => {
          const mesh = obj as THREE.Mesh;
          mesh.geometry?.dispose();
          const mat = mesh.material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat?.dispose();
        });
      }
      for (const child of boxGroup.children) {
        const mesh = child as THREE.Mesh;
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      }
      grains = null;
      cloud = null;
      chunks = null;
      shadow = null;
      heightfield = null;
      reducer = null;
      ground = null;
    },
  };
}
